import React, { useState, useMemo, useEffect } from 'react';
import { User } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useDatasetPackages, DatasetPackage } from '../hooks/useDatasetPackages';
import { usePhotoPackages, PhotoPackage } from '../hooks/usePhotoPackages';
import { useVideoPackages, VideoPackage } from '../hooks/useVideoPackages';
import { getClientCredits, purchaseMarketplaceNode } from '../lib/callables';
import { useOwnedSubmissions } from '../hooks/useOwnedSubmissions';
import { useCategoryNames } from '../hooks/useCategoryNames';
import RawPhotoBrowse, { useReleasedRawPhotos, useReleasedAnnotatedPhotos, useReleasedRawVideos, RawPhoto } from './RawPhotoBrowse';
import WhatYouGet from '../components/WhatYouGet';
import './Marketplace.css';

const CREDITS_PER_LABEL = 10;
const CREDITS_PER_PHOTO = 1;

type MarketplaceProps = {
  user: User;
  sidebarOpen?: boolean;
};

type ActiveTab = 'datasets' | 'photos' | 'videos';
type PackageUnion = DatasetPackage | PhotoPackage | VideoPackage;

export default function Marketplace({ user, sidebarOpen = true }: MarketplaceProps) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<ActiveTab>('photos');
  
  // Package data
  const { packages: datasetPackages, loading: datasetsLoading } = useDatasetPackages();
  const { packages: photoPackages, loading: photosLoading } = usePhotoPackages();
  const { packages: videoPackages, loading: videosLoading } = useVideoPackages();
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  
  // Selection state - separate for each tab
  const [selectedDatasetPackageIds, setSelectedDatasetPackageIds] = useState<Set<string>>(new Set());
  const [selectedPhotoPackageIds, setSelectedPhotoPackageIds] = useState<Set<string>>(new Set());
  const [selectedVideoPackageIds, setSelectedVideoPackageIds] = useState<Set<string>>(new Set());
  
  // Credits
  const [creditsBalance, setCreditsBalance] = useState<number>(0);
  const [creditsLoading, setCreditsLoading] = useState<boolean>(true);

  // Raw Photos marketplace state (lifted so the sticky footer can read selection)
  const { photos: rawPhotos, loading: rawPhotosLoading } = useReleasedRawPhotos();
  const { ownedIds, rawOwnedIds, annotatedOwnedIds, rawVideoOwnedIds } = useOwnedSubmissions(user.uid);
  const { resolve: resolveCategoryName, resolveLabelCategory, resolveLabelSubcategory } = useCategoryNames();
  const [rawSelected, setRawSelected] = useState<Set<string>>(new Set());
  const [rawExpanded, setRawExpanded] = useState<Set<string>>(new Set());
  const [rawFilters, setRawFilters] = useState({ category: 'all', subcategory: 'all', itemType: 'all', task: 'all' });

  const toggleRawIds = (ids: string[], select: boolean) => {
    setRawSelected((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };
  const toggleRawExpand = (key: string) => {
    setRawExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const rawSelectedCount = rawSelected.size;
  const rawTotalCredits = useMemo(
    () => rawPhotos.reduce((sum, p) => (rawSelected.has(p.id) ? sum + (p.credits ?? 0) : sum), 0),
    [rawPhotos, rawSelected]
  );
  const [purchasing, setPurchasing] = useState<boolean>(false);

  // Raw Videos marketplace state
  const { photos: rawVideos, loading: rawVideosLoading } = useReleasedRawVideos();
  const [rawVideoSelected, setRawVideoSelected] = useState<Set<string>>(new Set());
  const [rawVideoExpanded, setRawVideoExpanded] = useState<Set<string>>(new Set());
  const [rawVideoFilters, setRawVideoFilters] = useState({ category: 'all', subcategory: 'all', itemType: 'all', task: 'all' });
  const toggleRawVideoIds = (ids: string[], select: boolean) => {
    setRawVideoSelected((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };
  const toggleRawVideoExpand = (key: string) => {
    setRawVideoExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const rawVideoSelectedCount = rawVideoSelected.size;
  const rawVideoTotalCredits = useMemo(
    () => rawVideos.reduce((sum, p) => (rawVideoSelected.has(p.id) ? sum + (p.credits ?? 0) : sum), 0),
    [rawVideos, rawVideoSelected]
  );
  const [videoPurchasing, setVideoPurchasing] = useState(false);

  // Annotated Photos marketplace state (lifted so the sticky footer can read selection)
  const { photos: annotatedPhotos, loading: annotatedLoading } = useReleasedAnnotatedPhotos();
  const [annotatedSelected, setAnnotatedSelected] = useState<Set<string>>(new Set());
  const [annotatedExpanded, setAnnotatedExpanded] = useState<Set<string>>(new Set());
  const [annotatedFilters, setAnnotatedFilters] = useState({ category: 'all', subcategory: 'all', itemType: 'all', task: 'all' });

  const toggleAnnotatedIds = (ids: string[], select: boolean) => {
    setAnnotatedSelected((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };
  const toggleAnnotatedExpand = (key: string) => {
    setAnnotatedExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Per-photo credit lookup for annotated (labels × 10, carried on each photo.credits)
  const annotatedCreditById = useMemo(() => {
    const m = new Map<string, number>();
    annotatedPhotos.forEach((p) => m.set(p.id, p.credits ?? 0));
    return m;
  }, [annotatedPhotos]);

  const annotatedLabelById = useMemo(() => {
    const m = new Map<string, number>();
    annotatedPhotos.forEach((p) => m.set(p.id, typeof p.labelCount === 'number' ? p.labelCount : 0));
    return m;
  }, [annotatedPhotos]);

  const annotatedSelectedCount = annotatedSelected.size;
  const annotatedTotalCredits = useMemo(() => {
    let sum = 0;
    annotatedSelected.forEach((id) => { sum += annotatedCreditById.get(id) ?? 0; });
    return sum;
  }, [annotatedSelected, annotatedCreditById]);

  const annotatedTotalLabels = useMemo(() => {
    let sum = 0;
    annotatedSelected.forEach((id) => { sum += annotatedLabelById.get(id) ?? 0; });
    return sum;
  }, [annotatedSelected, annotatedLabelById]);

  const [annotatedPurchasing, setAnnotatedPurchasing] = useState(false);

  // Pre-select tab (+ optional tree category) when arriving from Dashboard
  useEffect(() => {
    const incomingCategory =
      typeof location.state?.categoryFilter === 'string' ? location.state.categoryFilter.trim() : '';
    const rawTab = location.state?.typeTab;
    const hasTab = rawTab === 'datasets' || rawTab === 'videos' || rawTab === 'photos';
    if (!incomingCategory && !hasTab) return;

    const incomingTab: ActiveTab = hasTab ? rawTab : 'photos';
    setActiveTab(incomingTab);

    if (incomingCategory) {
      const nextFilters = {
        category: incomingCategory,
        subcategory: 'all',
        itemType: 'all',
        task: 'all',
      };
      if (incomingTab === 'photos') setRawFilters(nextFilters);
      else if (incomingTab === 'datasets') setAnnotatedFilters(nextFilters);
      else setRawVideoFilters(nextFilters);
      setSelectedCategory(incomingCategory);
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // Fetch credits balance — reusable so purchase flow can refresh after debit
  const refreshCredits = async () => {
    try {
      setCreditsLoading(true);
      const data = await getClientCredits();
      setCreditsBalance(data.creditBalance || 0);
      // Navigation holds its own balance state and cannot see this one.
      // Broadcast so the header refetches instead of showing a stale number.
      window.dispatchEvent(new CustomEvent('pixano:credits-changed'));
    } catch (error) {
      console.error('Failed to load credit balance:', error);
      setCreditsBalance(0);
    } finally {
      setCreditsLoading(false);
    }
  };

  useEffect(() => {
    refreshCredits();
  }, []);

  const handlePurchaseRawPhotos = async () => {
    if (rawSelectedCount === 0) return;
    if (creditsBalance < rawTotalCredits) {
      alert(`Not enough credits. This purchase needs ${rawTotalCredits} credits but you have ${creditsBalance}.`);
      return;
    }
    const ok = window.confirm(
      `Purchase ${rawSelectedCount} photo${rawSelectedCount === 1 ? '' : 's'} for ${rawTotalCredits} credit${rawTotalCredits === 1 ? '' : 's'}?`
    );
    if (!ok) return;
    setPurchasing(true);
    try {
      const ids = Array.from(rawSelected);
      const result = await purchaseMarketplaceNode(ids);
      await refreshCredits();
      setRawSelected(new Set());
      alert(result.message || `Purchased ${result.purchased} photos.`);
    } catch (error: any) {
      console.error('Purchase failed:', error);
      alert(error?.message || 'Purchase failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handlePurchaseRawVideos = async () => {
    if (rawVideoSelectedCount === 0) return;
    if (creditsBalance < rawVideoTotalCredits) {
      alert(`Not enough credits. This purchase needs ${rawVideoTotalCredits} credits but you have ${creditsBalance}.`);
      return;
    }
    const ok = window.confirm(
      `Purchase ${rawVideoSelectedCount} video${rawVideoSelectedCount === 1 ? '' : 's'} for ${rawVideoTotalCredits} credit${rawVideoTotalCredits === 1 ? '' : 's'}?`
    );
    if (!ok) return;
    setVideoPurchasing(true);
    try {
      const ids = Array.from(rawVideoSelected);
      const result = await purchaseMarketplaceNode(ids, 'rawVideo');
      await refreshCredits();
      setRawVideoSelected(new Set());
      alert(result.message || `Purchased ${result.purchased} videos.`);
    } catch (error: any) {
      console.error('Purchase failed:', error);
      alert(error?.message || 'Purchase failed. Please try again.');
    } finally {
      setVideoPurchasing(false);
    }
  };

  const handlePurchaseAnnotated = async () => {
    if (annotatedSelectedCount === 0) return;
    if (creditsBalance < annotatedTotalCredits) {
      alert(`Not enough credits. This purchase needs ${annotatedTotalCredits} credits but you have ${creditsBalance}.`);
      return;
    }
    const ok = window.confirm(`Purchase ${annotatedSelectedCount} annotated photo${annotatedSelectedCount === 1 ? '' : 's'} for ${annotatedTotalCredits} credits?`);
    if (!ok) return;
    setAnnotatedPurchasing(true);
    try {
      const ids = Array.from(annotatedSelected);
      const result = await purchaseMarketplaceNode(ids, 'annotatedPhoto');
      await refreshCredits();
      setAnnotatedSelected(new Set());
      alert(result.message || `Purchased ${result.purchased} annotated photos.`);
    } catch (error: any) {
      console.error('Annotated purchase failed:', error);
      alert(error?.message || 'Purchase failed. Please try again.');
    } finally {
      setAnnotatedPurchasing(false);
    }
  };

  // Get current packages based on active tab
  const currentPackages = useMemo<PackageUnion[]>(() => {
    if (activeTab === 'datasets') return datasetPackages;
    if (activeTab === 'photos') return photoPackages;
    return videoPackages;
  }, [activeTab, datasetPackages, photoPackages, videoPackages]);

  const loading = activeTab === 'datasets' ? datasetsLoading : activeTab === 'photos' ? photosLoading : videosLoading;

  // Get unique categories from current packages
  const getUniqueCategories = () => {
    const categories = new Set<string>();
    currentPackages.forEach((pkg: PackageUnion) => {
      if (pkg.stats?.categories && Array.isArray(pkg.stats.categories)) {
        pkg.stats.categories.forEach((cat: string) => {
          if (cat && cat !== 'N/A' && cat !== 'General') {
            categories.add(cat);
          }
        });
      }
    });
    return Array.from(categories).sort();
  };

  // Filter packages based on search and filters
  const getFilteredPackages = (): PackageUnion[] => {
    return currentPackages.filter((pkg: PackageUnion) => {
      // Show selected only filter
      const selectedIds = activeTab === 'datasets' ? selectedDatasetPackageIds : activeTab === 'photos' ? selectedPhotoPackageIds : selectedVideoPackageIds;
      if (showSelectedOnly && !selectedIds.has(pkg.id)) {
        return false;
      }
      
      // Category filter
      if (selectedCategory !== 'all') {
        const packageCategories = pkg.stats?.categories || [];
        if (!packageCategories.includes(selectedCategory)) {
          return false;
        }
      }
      
      // General search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const name = (pkg.name || '').toLowerCase();
        const description = (pkg.description || '').toLowerCase();
        const tags = (pkg.tags || []).join(' ').toLowerCase();
        const categories = (pkg.stats?.categories || []).join(' ').toLowerCase();
        
        if (!name.includes(query) && 
            !description.includes(query) && 
            !tags.includes(query) &&
            !categories.includes(query)) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || 
           selectedCategory !== 'all' ||
           showSelectedOnly;
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setShowSelectedOnly(false);
  };

  // Selection handlers
  const toggleSelection = (packageId: string) => {
    if (activeTab === 'datasets') {
      const newSelected = new Set(selectedDatasetPackageIds);
      if (newSelected.has(packageId)) {
        newSelected.delete(packageId);
      } else {
        newSelected.add(packageId);
      }
      setSelectedDatasetPackageIds(newSelected);
    } else if (activeTab === 'photos') {
      const newSelected = new Set(selectedPhotoPackageIds);
      if (newSelected.has(packageId)) {
        newSelected.delete(packageId);
      } else {
        newSelected.add(packageId);
      }
      setSelectedPhotoPackageIds(newSelected);
    } else {
      const newSelected = new Set(selectedVideoPackageIds);
      if (newSelected.has(packageId)) {
        newSelected.delete(packageId);
      } else {
        newSelected.add(packageId);
      }
      setSelectedVideoPackageIds(newSelected);
    }
  };

  const selectAll = () => {
    const filtered = getFilteredPackages();
    const filteredIds = new Set<string>(filtered.map((pkg: PackageUnion) => pkg.id));
    const selectedIds = activeTab === 'datasets' ? selectedDatasetPackageIds : activeTab === 'photos' ? selectedPhotoPackageIds : selectedVideoPackageIds;
    
    if (filteredIds.size > 0 && Array.from(filteredIds).every((id: string) => selectedIds.has(id))) {
      // Deselect all filtered items
      if (activeTab === 'datasets') {
        const newSelected = new Set(selectedDatasetPackageIds);
        filteredIds.forEach((id: string) => newSelected.delete(id));
        setSelectedDatasetPackageIds(newSelected);
      } else {
        const newSelected = new Set(selectedPhotoPackageIds);
        filteredIds.forEach((id: string) => newSelected.delete(id));
        setSelectedPhotoPackageIds(newSelected);
      }
    } else {
      // Select all filtered items
      if (activeTab === 'datasets') {
        const newSelected = new Set(selectedDatasetPackageIds);
        filteredIds.forEach((id: string) => newSelected.add(id));
        setSelectedDatasetPackageIds(newSelected);
      } else {
        const newSelected = new Set(selectedPhotoPackageIds);
        filteredIds.forEach((id: string) => newSelected.add(id));
        setSelectedPhotoPackageIds(newSelected);
      }
    }
  };

  const getSelectedStats = () => {
    const filtered = getFilteredPackages();
    const selectedIds = activeTab === 'datasets' ? selectedDatasetPackageIds : selectedPhotoPackageIds;
    const selected = filtered.filter((pkg: PackageUnion) => selectedIds.has(pkg.id));
    
    if (activeTab === 'datasets') {
      const datasetSelected = selected as DatasetPackage[];
      const totalLabels = datasetSelected.reduce((sum: number, pkg: DatasetPackage) => sum + (pkg.stats?.labels || 0), 0);
      const totalImages = datasetSelected.reduce((sum: number, pkg: DatasetPackage) => sum + (pkg.stats?.images || 0), 0);
      // Calculate cost: use priceUsd if available, otherwise labels * CREDITS_PER_LABEL
      const totalPrice = datasetSelected.reduce((sum: number, pkg: DatasetPackage) => {
        if (pkg.priceUsd) {
          // Convert USD to credits (assuming 1 USD = 100 credits, adjust as needed)
          return sum + (pkg.priceUsd * 100);
        }
        return sum + ((pkg.stats?.labels || 0) * CREDITS_PER_LABEL);
      }, 0);
      return { count: selected.length, totalImages, totalLabels, totalPrice };
    } else if (activeTab === 'photos') {
      const photoSelected = selected as PhotoPackage[];
      const totalImages = photoSelected.reduce((sum: number, pkg: PhotoPackage) => sum + (pkg.stats?.images || 0), 0);
      // Calculate cost: use priceUsd if available, otherwise images * CREDITS_PER_PHOTO
      const totalPrice = photoSelected.reduce((sum: number, pkg: PhotoPackage) => {
        if (pkg.priceUsd) {
          return sum + (pkg.priceUsd * 100);
        }
        return sum + ((pkg.stats?.images || 0) * CREDITS_PER_PHOTO);
      }, 0);
      return { count: selected.length, totalImages, totalLabels: 0, totalPrice };
    } else {
      const videoSelected = selected as VideoPackage[];
      const totalVideos = videoSelected.reduce((sum: number, pkg: VideoPackage) => sum + (pkg.stats?.videos || 0), 0);
      // Calculate cost: use priceUsd if available, otherwise videos * CREDITS_PER_PHOTO (same rate as photos)
      const totalPrice = videoSelected.reduce((sum: number, pkg: VideoPackage) => {
        if (pkg.priceUsd) {
          return sum + (pkg.priceUsd * 100);
        }
        return sum + ((pkg.stats?.videos || 0) * CREDITS_PER_PHOTO);
      }, 0);
      return { count: selected.length, totalImages: totalVideos, totalLabels: 0, totalPrice };
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString();
      }
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
      }
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleDateString();
      }
      return 'N/A';
    } catch {
      return 'N/A';
    }
  };

  // Helper to get category display
  const getCategoryDisplay = (pkg: PackageUnion) => {
    const categories = pkg.stats?.categories || [];
    if (categories.length === 0) return '-';
    if (categories.length === 1) return categories[0];
    return 'Multiple Categories';
  };

  // Helper to get tasks included display
  const getTasksIncludedDisplay = (pkg: PackageUnion) => {
    const categories = pkg.stats?.categories || [];
    const count = categories.length;
    if (count === 0) return 'N/A';
    return `${count} ${count === 1 ? 'category' : 'categories'}`;
  };

  // Helper to calculate cost
  const getPackageCost = (pkg: PackageUnion) => {
    if (pkg.priceUsd) {
      // Convert USD to credits (assuming 1 USD = 100 credits)
      return pkg.priceUsd * 100;
    }
    if (activeTab === 'datasets') {
      const datasetPkg = pkg as DatasetPackage;
      return (datasetPkg.stats?.labels || 0) * CREDITS_PER_LABEL;
    } else if (activeTab === 'photos') {
      return ((pkg as PhotoPackage).stats?.images || 0) * CREDITS_PER_PHOTO;
    } else {
      return ((pkg as VideoPackage).stats?.videos || 0) * CREDITS_PER_PHOTO;
    }
  };

  const filteredPackages = getFilteredPackages();
  const stats = getSelectedStats();
  const selectedIds = activeTab === 'datasets' ? selectedDatasetPackageIds : activeTab === 'photos' ? selectedPhotoPackageIds : selectedVideoPackageIds;

  return (
    <div className="marketplace-page" style={{ background: colors.background, color: colors.text }}>
      <div className="marketplace-header">
        <div>
          <h1>Dataset Marketplace</h1>
          <p>
            {activeTab === 'datasets'
              ? 'Browse and purchase human-verified annotated photos. Credit cost is shown per photo.'
              : activeTab === 'photos'
              ? 'Browse and purchase individual raw photos. Credit cost is shown per photo.'
              : 'Browse and purchase raw video clips. Credit cost is shown per clip.'}
          </p>
        </div>
        {activeTab !== 'photos' && activeTab !== 'datasets' && activeTab !== 'videos' && (
        <button
          onClick={selectAll}
          className="select-all-btn-header"
          style={{
            background: colors.card,
            borderColor: colors.border,
            color: colors.text,
            padding: '10px 20px',
            borderRadius: '6px',
            border: '1px solid',
            fontSize: '14px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            marginLeft: '20px',
            fontWeight: '500'
          }}
          title="Select or deselect all visible items"
        >
          {(() => {
            const allFilteredSelected = filteredPackages.length > 0 && 
              filteredPackages.every((pkg: PackageUnion) => selectedIds.has(pkg.id));
            return allFilteredSelected ? 'Deselect All' : 'Select All';
          })()}
        </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '20px',
        borderBottom: `2px solid ${colors.border}`,
        paddingBottom: '0'
      }}>
        <button
          onClick={() => {
            setActiveTab('photos');
            setSelectedCategory('all');
            setSearchQuery('');
          }}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'photos' ? `3px solid ${colors.primary}` : '3px solid transparent',
            color: activeTab === 'photos' ? colors.primary : colors.secondary,
            cursor: 'pointer',
            fontWeight: activeTab === 'photos' ? '600' : '400',
            fontSize: '16px',
            transition: 'all 0.2s'
          }}
        >
          Raw Photos
        </button>
        <button
          onClick={() => {
            setActiveTab('datasets');
            setSelectedCategory('all');
            setSearchQuery('');
          }}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'datasets' ? `3px solid ${colors.primary}` : '3px solid transparent',
            color: activeTab === 'datasets' ? colors.primary : colors.secondary,
            cursor: 'pointer',
            fontWeight: activeTab === 'datasets' ? '600' : '400',
            fontSize: '16px',
            transition: 'all 0.2s'
          }}
        >
          Annotated Photos
        </button>
        <button
          onClick={() => {
            setActiveTab('videos');
            setSelectedCategory('all');
            setSearchQuery('');
          }}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'videos' ? `3px solid ${colors.primary}` : '3px solid transparent',
            color: activeTab === 'videos' ? colors.primary : colors.secondary,
            cursor: 'pointer',
            fontWeight: activeTab === 'videos' ? '600' : '400',
            fontSize: '16px',
            transition: 'all 0.2s'
          }}
        >
          Raw Video
        </button>
        <button
          disabled
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: '3px solid transparent',
            color: colors.secondary,
            cursor: 'not-allowed',
            fontWeight: '400',
            fontSize: '16px',
            opacity: 0.5,
          }}
        >
          Annotated Video (Coming Soon)
        </button>
      </div>

      {/* Filters and Search */}
      {activeTab !== 'photos' && activeTab !== 'datasets' && activeTab !== 'videos' && (
      <div className="filters-section" style={{ marginBottom: '20px' }}>
        <div className="filters-row">
          <div className="filter-group">
            <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              Search
            </label>
            <input
              type="text"
              placeholder="Search by name, description, tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              style={{
                background: colors.card,
                borderColor: colors.border,
                color: colors.text,
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid',
                fontSize: '14px',
                width: '100%',
                maxWidth: '300px'
              }}
            />
          </div>
          
          <div className="filter-group">
            <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="filter-select"
              style={{
                background: colors.card,
                borderColor: colors.border,
                color: colors.text,
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Categories</option>
              {getUniqueCategories().map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => setShowSelectedOnly(!showSelectedOnly)}
              className="show-selected-btn"
              style={{
                background: showSelectedOnly ? colors.primary : colors.card,
                borderColor: colors.border,
                color: showSelectedOnly ? '#fff' : colors.text,
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid',
                fontSize: '14px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: showSelectedOnly ? '600' : '400'
              }}
            >
              Show Selected ({selectedIds.size})
            </button>
          </div>
        </div>
        
        {/* Filter Indicators */}
        {hasActiveFilters() && (
          <div className="filter-indicators" style={{ 
            marginTop: '12px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <span style={{ color: colors.secondary, fontSize: '12px', fontWeight: '500' }}>
              Active Filters:
            </span>
            
            {searchQuery.trim() && (
              <span className="filter-badge" style={{
                background: colors.primary + '20',
                color: colors.primary,
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                Search: "{searchQuery}"
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: colors.primary,
                    cursor: 'pointer',
                    padding: '0',
                    marginLeft: '4px',
                    fontSize: '14px',
                    lineHeight: '1'
                  }}
                  title="Remove filter"
                >
                  ×
                </button>
              </span>
            )}
            
            {selectedCategory !== 'all' && (
              <span className="filter-badge" style={{
                background: colors.primary + '20',
                color: colors.primary,
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                Category: {selectedCategory}
                <button
                  onClick={() => setSelectedCategory('all')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: colors.primary,
                    cursor: 'pointer',
                    padding: '0',
                    marginLeft: '4px',
                    fontSize: '14px',
                    lineHeight: '1'
                  }}
                  title="Remove filter"
                >
                  ×
                </button>
              </span>
            )}
            
            {showSelectedOnly && (
              <span className="filter-badge" style={{
                background: colors.primary + '20',
                color: colors.primary,
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                Show Selected Only
                <button
                  onClick={() => setShowSelectedOnly(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: colors.primary,
                    cursor: 'pointer',
                    padding: '0',
                    marginLeft: '4px',
                    fontSize: '14px',
                    lineHeight: '1'
                  }}
                  title="Remove filter"
                >
                  ×
                </button>
              </span>
            )}
            
            <button
              onClick={clearAllFilters}
              className="clear-filters-btn"
              style={{
                background: 'transparent',
                borderColor: colors.border,
                color: colors.secondary,
                padding: '4px 12px',
                borderRadius: '6px',
                border: '1px solid',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                marginLeft: '8px'
              }}
            >
              Clear All Filters
            </button>
        </div>
        )}
      </div>
      )}

      {activeTab !== 'photos' && activeTab !== 'datasets' && activeTab !== 'videos' && loading ? (
        <div className="loading">Loading {activeTab}...</div>
      ) : activeTab !== 'photos' && activeTab !== 'datasets' && activeTab !== 'videos' && filteredPackages.length === 0 ? (
        <div className="empty-state" style={{ color: colors.secondary }}>
          {currentPackages.length === 0
            ? `No video packages are currently available. Packages must be created and published in the admin portal.`
            : 'No packages match your filters. Adjust filters to see more data.'}
        </div>
      ) : (
        <>
          {activeTab === 'photos' ? (
            <>
            <WhatYouGet tier="rawPhoto" />
            <RawPhotoBrowse
              photos={rawPhotos}
              loading={rawPhotosLoading}
              selected={rawSelected}
              onToggleIds={toggleRawIds}
              filters={rawFilters}
              onFilterChange={setRawFilters}
              expanded={rawExpanded}
              onToggleExpand={toggleRawExpand}
              ownedIds={rawOwnedIds}
            />
            </>
          ) : activeTab === 'datasets' ? (
            <>
            <WhatYouGet tier="annotatedPhoto" />
            <RawPhotoBrowse
              mode="purchase"
              photos={annotatedPhotos}
              loading={annotatedLoading}
              selected={annotatedSelected}
              onToggleIds={toggleAnnotatedIds}
              filters={annotatedFilters}
              onFilterChange={setAnnotatedFilters}
              expanded={annotatedExpanded}
              onToggleExpand={toggleAnnotatedExpand}
              ownedIds={annotatedOwnedIds}
              labelNameResolver={resolveCategoryName}
              resolveLabelCategory={resolveLabelCategory}
              resolveLabelSubcategory={resolveLabelSubcategory}
            />
            </>
          ) : activeTab === 'videos' ? (
            <>
            <WhatYouGet tier="rawVideo" />
            <RawPhotoBrowse
              mode="purchase"
              photos={rawVideos}
              loading={rawVideosLoading}
              selected={rawVideoSelected}
              onToggleIds={toggleRawVideoIds}
              filters={rawVideoFilters}
              onFilterChange={setRawVideoFilters}
              expanded={rawVideoExpanded}
              onToggleExpand={toggleRawVideoExpand}
              ownedIds={rawVideoOwnedIds}
              unitLabel="video"
              showDuration={true}
            />
            </>
          ) : (
            <div className="submissions-table">
              <table>
                <thead>
                  <tr style={{ background: colors.card, borderColor: colors.border }}>
                    <th style={{ borderColor: colors.border }}>Select</th>
                    <th style={{ borderColor: colors.border }}>Thumbnail</th>
                    <th style={{ borderColor: colors.border }}>Category</th>
                    <th style={{ borderColor: colors.border }}>Subcategory</th>
                    <th style={{ borderColor: colors.border }}>Total Images</th>
                    <th style={{ borderColor: colors.border }}>Tasks Included</th>
                    <th style={{ borderColor: colors.border }}>Last Updated</th>
                    <th style={{ borderColor: colors.border }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPackages.map((pkg: PackageUnion) => {
                    const isSelected = selectedIds.has(pkg.id);
                    const cost = getPackageCost(pkg);

                    return (
                      <tr
                        key={pkg.id}
                        style={{
                          background: isSelected ? colors.primary + '10' : colors.card,
                          borderColor: colors.border,
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          // Don't navigate if clicking on checkbox
                          if ((e.target as HTMLElement).tagName === 'INPUT') return;
                          navigate(`/marketplace/${pkg.id}`);
                        }}
                      >
                        <td style={{ borderColor: colors.border }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelection(pkg.id)}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          <div className="no-thumbnail" style={{ 
                            background: colors.border,
                            width: '60px',
                            height: '60px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: colors.secondary
                          }}>
                            {pkg.name?.[0]?.toUpperCase() || '?'}
                          </div>
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {getCategoryDisplay(pkg)}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          Video Package
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {((pkg as VideoPackage).stats?.videos || 0).toLocaleString()}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          <div className="task-info">
                            <div className="task-title" title={getTasksIncludedDisplay(pkg)}>
                              {getTasksIncludedDisplay(pkg)}
                            </div>
                          </div>
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {formatDate(pkg.createdAt)}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {cost.toLocaleString()} credits
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab !== 'photos' && activeTab !== 'datasets' && activeTab !== 'videos' && (
          <div
            className="sticky-footer"
            style={{
              background: colors.card,
              borderColor: colors.border,
              left: sidebarOpen ? '220px' : '72px',
              transition: 'left 0.3s ease'
            }}
          >
            <div className="footer-stats">
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>
                  Selected Packages:
                </span>
                <span className="stat-value" style={{ color: colors.text }}>{stats.count}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Total Images:</span>
                <span className="stat-value" style={{ color: colors.text }}>{stats.totalImages.toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Total Credits:</span>
                <span className="stat-value" style={{ color: colors.primary, fontWeight: 'bold' }}>
                  {stats.totalPrice.toLocaleString()}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Remaining Credits:</span>
                <span 
                  className="stat-value" 
                  style={{ 
                    color: creditsLoading 
                      ? colors.primary 
                      : (creditsBalance - stats.totalPrice) < 0 
                        ? '#ff4757' 
                        : colors.primary, 
                    fontWeight: 'bold' 
                  }}
                >
                  {creditsLoading 
                    ? 'Loading…' 
                    : (creditsBalance - stats.totalPrice).toLocaleString()}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  if (selectedIds.size === 0) {
                    alert(`Please select at least one package to purchase.`);
                    return;
                  }
                  alert(`Purchase functionality coming soon! This will add selected packages to your library.`);
                }}
                disabled={selectedIds.size === 0}
                className="download-btn"
                style={{
                  background: selectedIds.size === 0 ? colors.border : colors.primary,
                  color: '#fff',
                  opacity: selectedIds.size === 0 ? 0.6 : 1,
                  cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                Purchase Selected
              </button>
            </div>
          </div>
          )}

          {activeTab === 'photos' && (
          <div
            className="sticky-footer"
            style={{
              background: colors.card,
              borderColor: colors.border,
              left: sidebarOpen ? '220px' : '72px',
              transition: 'left 0.3s ease'
            }}
          >
            <div className="footer-stats">
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Photos Selected:</span>
                <span className="stat-value" style={{ color: colors.text }}>{rawSelectedCount}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Total Credits:</span>
                <span className="stat-value" style={{ color: colors.primary, fontWeight: 'bold' }}>
                  {rawTotalCredits.toLocaleString()}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Remaining Credits:</span>
                <span
                  className="stat-value"
                  style={{
                    color: creditsLoading
                      ? colors.primary
                      : (creditsBalance - rawTotalCredits) < 0
                        ? '#ff4757'
                        : colors.primary,
                    fontWeight: 'bold'
                  }}
                >
                  {creditsLoading
                    ? 'Loading…'
                    : (creditsBalance - rawTotalCredits).toLocaleString()}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handlePurchaseRawPhotos}
                disabled={rawSelectedCount === 0 || purchasing}
                className="download-btn"
                style={{
                  background: (rawSelectedCount === 0 || purchasing) ? colors.border : colors.primary,
                  color: '#fff',
                  opacity: (rawSelectedCount === 0 || purchasing) ? 0.6 : 1,
                  cursor: (rawSelectedCount === 0 || purchasing) ? 'not-allowed' : 'pointer'
                }}
              >
                {purchasing ? 'Purchasing…' : 'Purchase Selected'}
              </button>
            </div>
          </div>
          )}

          {activeTab === 'datasets' && (
          <div
            className="sticky-footer"
            style={{
              background: colors.card,
              borderColor: colors.border,
              left: sidebarOpen ? '220px' : '72px',
              transition: 'left 0.3s ease'
            }}
          >
            <div className="footer-stats">
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Photos Selected:</span>
                <span className="stat-value" style={{ color: colors.text }}>{annotatedSelectedCount}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Total Labels:</span>
                <span className="stat-value" style={{ color: colors.text }}>{annotatedTotalLabels.toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Total Credits:</span>
                <span className="stat-value" style={{ color: colors.primary, fontWeight: 'bold' }}>
                  {annotatedTotalCredits.toLocaleString()}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Remaining Credits:</span>
                <span
                  className="stat-value"
                  style={{
                    color: creditsLoading
                      ? colors.primary
                      : (creditsBalance - annotatedTotalCredits) < 0
                        ? '#ff4757'
                        : colors.primary,
                    fontWeight: 'bold'
                  }}
                >
                  {creditsLoading
                    ? 'Loading…'
                    : (creditsBalance - annotatedTotalCredits).toLocaleString()}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handlePurchaseAnnotated}
                disabled={annotatedSelectedCount === 0 || annotatedPurchasing}
                className="download-btn"
                style={{
                  background: (annotatedSelectedCount === 0 || annotatedPurchasing) ? colors.border : colors.primary,
                  color: '#fff',
                  opacity: (annotatedSelectedCount === 0 || annotatedPurchasing) ? 0.6 : 1,
                  cursor: (annotatedSelectedCount === 0 || annotatedPurchasing) ? 'not-allowed' : 'pointer'
                }}
              >
                {annotatedPurchasing ? 'Purchasing…' : 'Purchase Selected'}
              </button>
            </div>
          </div>
          )}

          {activeTab === 'videos' && (
          <div
            className="sticky-footer"
            style={{
              background: colors.card,
              borderColor: colors.border,
              left: sidebarOpen ? '220px' : '72px',
              transition: 'left 0.3s ease'
            }}
          >
            <div className="footer-stats">
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Videos Selected:</span>
                <span className="stat-value" style={{ color: colors.text }}>{rawVideoSelectedCount}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Total Credits:</span>
                <span className="stat-value" style={{ color: colors.primary, fontWeight: 'bold' }}>
                  {rawVideoTotalCredits.toLocaleString()}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ color: colors.secondary }}>Remaining Credits:</span>
                <span
                  className="stat-value"
                  style={{
                    color: creditsLoading
                      ? colors.primary
                      : (creditsBalance - rawVideoTotalCredits) < 0
                        ? '#ff4757'
                        : colors.primary,
                    fontWeight: 'bold'
                  }}
                >
                  {creditsLoading
                    ? 'Loading…'
                    : (creditsBalance - rawVideoTotalCredits).toLocaleString()}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handlePurchaseRawVideos}
                disabled={rawVideoSelectedCount === 0 || videoPurchasing}
                className="download-btn"
                style={{
                  background: (rawVideoSelectedCount === 0 || videoPurchasing) ? colors.border : colors.primary,
                  color: '#fff',
                  opacity: (rawVideoSelectedCount === 0 || videoPurchasing) ? 0.6 : 1,
                  cursor: (rawVideoSelectedCount === 0 || videoPurchasing) ? 'not-allowed' : 'pointer'
                }}
              >
                {videoPurchasing ? 'Purchasing…' : 'Purchase Selected'}
              </button>
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}
